import { useState, useCallback } from 'react';
import { useFeedbackCollector } from '@atlassian/jira-feedback-collector/src/controllers/index.tsx';
import { useBacklogSelector } from '../../../../../state/index.tsx';
import { makeIsModalOpen } from '../../../../../state/selectors/modals/index.tsx';
import { GlobalIssueCreateType } from '../../../../../model/modals/types.tsx';

type Props = {
	isFocusMovedToAiPanel?: () => boolean;
	isPlaceholderLinkPickerOpen?: boolean;
	isFormVisibleOverride?: boolean;
	isFormVisibleByDefault?: boolean;
};
/**
 * ICC Form visibility control with Ai Work Creation flow
 * - Show form (`showForm`) on button trigger
 * - Close form (`hideForm`) on "cancel"
 * - Close form (`hideForm`) on blur, except if
 * 		1. focus moved to Ai Panel / Close icon in AI panel header is focused on mount of AI panel
 * 		2. Placeholder link picker is open
 * 		3. GIC is open
 * 		4. Feedback collector is open
 */
export const useIsFormVisible = ({
	isFocusMovedToAiPanel,
	isPlaceholderLinkPickerOpen,
	isFormVisibleOverride,
	isFormVisibleByDefault = false,
}: Props) => {
	const [isFormVisible, setIsFormVisible] = useState(isFormVisibleByDefault);
	const [{ isFeedbackCollectorOpen }] = useFeedbackCollector();
	const isModalOpen = useBacklogSelector(makeIsModalOpen);
	const isGlobalIssueCreateModal = isModalOpen(GlobalIssueCreateType);
	const shouldKeepIccOpenOnBlur = Boolean(
		isGlobalIssueCreateModal || isPlaceholderLinkPickerOpen || isFeedbackCollectorOpen,
	);

	const shouldSkipFormCloseOnBlur = useCallback(() => {
		return isFocusMovedToAiPanel?.() || shouldKeepIccOpenOnBlur;
	}, [isFocusMovedToAiPanel, shouldKeepIccOpenOnBlur]);

	const showForm = useCallback(() => {
		setIsFormVisible(true);
	}, []);

	const hideForm = useCallback(() => {
		setIsFormVisible(false);
	}, []);

	return {
		isFormVisible: isFormVisibleOverride ?? isFormVisible,
		showForm,
		hideForm,
		shouldSkipFormCloseOnBlur,
	};
};
